import { useState } from "react";
import {
  useCanAccess,
  useCreate,
  useDelete,
  useGetList,
  useNotify,
  useRefresh,
  useTranslate,
  useUpdate,
} from "ra-core";
import { Plus, Save, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import type { PublicHoliday } from "../types";

const RESOURCE = "public_holidays";

/**
 * Returns the validation message key for a holiday date, or undefined when valid.
 * One row per date: a date already used by another holiday is rejected.
 */
export const validateHolidayDate = (
  date: string,
  holidays: Pick<PublicHoliday, "id" | "date">[],
  ownId?: PublicHoliday["id"],
) => {
  if (!date) return "crm.settings.holidays.date_required";
  const isTaken = holidays.some(
    (holiday) => holiday.id !== ownId && holiday.date.slice(0, 10) === date,
  );
  return isTaken ? "crm.settings.holidays.duplicate" : undefined;
};

const HolidayRow = ({
  holiday,
  holidays,
  canWrite,
}: {
  holiday?: PublicHoliday;
  holidays: PublicHoliday[];
  canWrite: boolean;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [create, { isPending: isCreating }] = useCreate();
  const [update, { isPending: isUpdating }] = useUpdate();
  const [deleteOne, { isPending: isDeleting }] = useDelete();
  const initialDate = holiday?.date.slice(0, 10) ?? "";
  const initialName = holiday?.name ?? "";
  const [date, setDate] = useState(initialDate);
  const [name, setName] = useState(initialName);
  const [errorKey, setErrorKey] = useState<string>();
  const isBusy = isCreating || isUpdating || isDeleting;
  const isDirty = date !== initialDate || name !== initialName;

  const handlers = {
    onSuccess: () => {
      refresh();
      notify("crm.settings.holidays.saved");
    },
    onError: () =>
      notify("crm.settings.holidays.save_error", { type: "error" }),
  };

  const handleSave = () => {
    const error = validateHolidayDate(date, holidays, holiday?.id);
    setErrorKey(error);
    if (error) return;
    if (holiday) {
      update(
        RESOURCE,
        { id: holiday.id, data: { date, name }, previousData: holiday },
        handlers,
      );
      return;
    }
    create(
      RESOURCE,
      { data: { date, name } },
      {
        ...handlers,
        onSuccess: () => {
          setDate("");
          setName("");
          handlers.onSuccess();
        },
      },
    );
  };

  const handleDelete = () => {
    if (!holiday) return;
    deleteOne(RESOURCE, { id: holiday.id, previousData: holiday }, handlers);
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <Input
          type="date"
          className="w-40 shrink-0"
          aria-label={translate("crm.settings.holidays.date")}
          value={date}
          disabled={!canWrite}
          onChange={(event) => setDate(event.target.value)}
        />
        <Input
          type="text"
          className="flex-1"
          aria-label={translate("crm.settings.holidays.name")}
          placeholder={translate("crm.settings.holidays.name")}
          value={name}
          disabled={!canWrite}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            // This row lives inside the settings <form>: Enter must not submit it.
            if (event.key === "Enter") {
              event.preventDefault();
              handleSave();
            }
          }}
        />
        {canWrite ? (
          holiday ? (
            <>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={!isDirty || isBusy}
                onClick={handleSave}
              >
                <Save className="h-4 w-4 mr-1" />
                {translate("ra.action.save")}
              </Button>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                disabled={isBusy}
                aria-label={translate("crm.settings.holidays.remove")}
                onClick={handleDelete}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <Button
              type="button"
              size="sm"
              disabled={isBusy}
              onClick={handleSave}
            >
              <Plus className="h-4 w-4 mr-1" />
              {translate("crm.settings.holidays.add")}
            </Button>
          )
        ) : null}
      </div>
      {errorKey ? (
        <p role="alert" className="text-sm text-destructive">
          {translate(errorKey)}
        </p>
      ) : null}
    </div>
  );
};

/**
 * Settings section to manage the public holidays used by the 48-hour reply
 * clock. Everyone signed in sees the list (sorted by date); only admins get the
 * add, change and remove controls. The list starts empty and is never pre-filled.
 * It saves on its own and uses no <form>, so it can sit inside the settings form.
 */
export const PublicHolidaysSettings = () => {
  const translate = useTranslate();
  const { canAccess: canWrite } = useCanAccess({
    resource: RESOURCE,
    action: "create",
  });
  const { data: holidays = [] } = useGetList<PublicHoliday>(RESOURCE, {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "date", order: "ASC" },
  });

  return (
    <div className="space-y-3">
      {holidays.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate("crm.settings.holidays.empty")}
        </p>
      ) : null}
      {holidays.map((holiday) => (
        <HolidayRow
          key={`${holiday.id}-${holiday.date}-${holiday.name}`}
          holiday={holiday}
          holidays={holidays}
          canWrite={canWrite === true}
        />
      ))}
      {canWrite === true ? <HolidayRow holidays={holidays} canWrite /> : null}
    </div>
  );
};
